import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography, Button, Link as MuiLink, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { useOpenContactDialog } from '../contexts/ContactDialogContext';
import {
    INSTAGRAM_PLATFORM_BADGE,
    PRESS_LINKS,
    PRESS_LOGOS,
    PRESS_MENTIONS,
    PRESS_QUOTES,
    type PressQuote,
} from './landingConstants';
import { PRODUCT_LANDING_CARDS } from './productPages';

const SUMMA = {
    fontHeading: '"Inter", sans-serif',
    fontBody: '"Inter", sans-serif',
    bg: '#ffffff',
    bgAlt: '#f7f6fb',
    text: '#121118',
    muted: 'rgba(18,17,24,0.55)',
    mutedSoft: 'rgba(18,17,24,0.45)',
    accent: '#652DD0',
    accentHover: '#5524B8',
    accentSoft: 'rgba(101,45,208,0.1)',
    border: '0.5px solid rgba(18,17,24,0.1)',
    sectionPaddingX: '1.25rem',
} as const;

const PRESS_QUOTE_CAROUSEL_GAP_PX = 24;
const heroButtonMinHeight = 44;
const PRESS_MENTION_ICON_PX = 40;

const pressQuoteCardSx = {
    flex: '0 0 auto',
    width: { xs: 280, sm: 320, md: 340 },
    background: SUMMA.bg,
    border: SUMMA.border,
    borderRadius: '14px',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
} as const;

const pressQuoteBlockquoteSx = {
    fontFamily: SUMMA.fontHeading,
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: '1.125rem',
    lineHeight: 1.5,
    color: SUMMA.text,
    mb: 2,
    flex: 1,
    display: '-webkit-box',
    WebkitLineClamp: 5,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
} as const;

const summaBrandMarkSx = {
    fontFamily: SUMMA.fontHeading,
    fontStyle: 'normal',
    fontWeight: 700,
    color: SUMMA.accent,
} as const;

/** Reword press quotes for Summa branding (source still mentions Podscope). */
function toSummaQuoteText(text: string): string {
    return text.replace(/@podscope\.co/gi, 'Summa').replace(/Podscope/gi, 'Summa');
}

function highlightSummaBrand(text: string): React.ReactNode {
    const parts = toSummaQuoteText(text).split(/(Summa)/gi);
    return parts.map((part, index) => {
        if (/^Summa$/i.test(part)) {
            return (
                <Box key={index} component="span" sx={summaBrandMarkSx}>
                    {part}
                </Box>
            );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
}

/** Ranking-asset style thumb: SVG cover + badge with cutout gap (no CSS border). */
function PressThumbWithBadge({
    coverUrl,
    badgeUrl,
    sizePx = 48,
    alt = '',
}: {
    coverUrl: string;
    badgeUrl?: string;
    sizePx?: number;
    alt?: string;
}) {
    const reactId = React.useId().replace(/:/g, '');
    const maskId = `summa-press-thumb-cutout-${reactId}`;

    const designThumb = 100;
    const designBadge = 34;
    const designOverflow = 8;
    const designGap = 3;
    const designBadgeRx = 12;
    const designThumbRx = 20;

    const badge = {
        x: -designOverflow,
        y: designThumb - designBadge + designOverflow,
        size: designBadge,
        rx: designBadgeRx,
    };
    const cutout = {
        x: badge.x - designGap,
        y: badge.y - designGap,
        size: badge.size + designGap * 2,
        rx: badge.rx + designGap,
    };

    if (!badgeUrl) {
        return (
            <Box
                sx={{
                    width: sizePx,
                    height: sizePx,
                    borderRadius: `${Math.round(sizePx * 0.2)}px`,
                    overflow: 'hidden',
                    bgcolor: SUMMA.bgAlt,
                    flexShrink: 0,
                }}
            >
                <Box
                    component="img"
                    src={coverUrl}
                    alt={alt}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            </Box>
        );
    }

    const xhtmlImg = (src: string, w: number, h: number, borderRadius = 0) =>
        React.createElement(
            'div',
            {
                xmlns: 'http://www.w3.org/1999/xhtml',
                style: {
                    width: w,
                    height: h,
                    margin: 0,
                    padding: 0,
                    overflow: 'hidden',
                    borderRadius,
                    backgroundColor: '#eceaf5',
                },
            },
            React.createElement('img', {
                src,
                alt: '',
                style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                },
            })
        );

    return (
        <Box
            sx={{
                position: 'relative',
                width: sizePx,
                height: sizePx,
                flexShrink: 0,
                overflow: 'visible',
            }}
        >
            <Box
                component="svg"
                width={designThumb}
                height={designThumb}
                viewBox={`0 0 ${designThumb} ${designThumb}`}
                overflow="visible"
                sx={{
                    display: 'block',
                    width: sizePx,
                    height: sizePx,
                    overflow: 'visible',
                }}
            >
                <defs>
                    <clipPath id={`${maskId}-clip`} clipPathUnits="userSpaceOnUse">
                        <rect
                            width={designThumb}
                            height={designThumb}
                            rx={designThumbRx}
                            ry={designThumbRx}
                        />
                    </clipPath>
                    <mask id={maskId} maskUnits="userSpaceOnUse">
                        <rect width={designThumb} height={designThumb} fill="white" />
                        <rect
                            x={cutout.x}
                            y={cutout.y}
                            width={cutout.size}
                            height={cutout.size}
                            rx={cutout.rx}
                            ry={cutout.rx}
                            fill="black"
                        />
                    </mask>
                </defs>
                <g clipPath={`url(#${maskId}-clip)`}>
                    <foreignObject
                        width={designThumb}
                        height={designThumb}
                        mask={`url(#${maskId})`}
                    >
                        {xhtmlImg(coverUrl, designThumb, designThumb)}
                    </foreignObject>
                </g>
                <foreignObject x={badge.x} y={badge.y} width={badge.size} height={badge.size}>
                    {xhtmlImg(badgeUrl, badge.size, badge.size, badge.rx)}
                </foreignObject>
            </Box>
        </Box>
    );
}

const pressMentionPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: PRESS_MENTION_ICON_PX + 20,
    height: PRESS_MENTION_ICON_PX + 20,
    boxSizing: 'border-box' as const,
    borderRadius: '12px',
    border: SUMMA.border,
    background: SUMMA.bgAlt,
    textDecoration: 'none',
    overflow: 'visible',
    transition: 'background 0.2s ease, border-color 0.2s ease',
    '&:hover': {
        background: 'rgba(101,45,208,0.08)',
        borderColor: 'rgba(101,45,208,0.35)',
    },
} as const;

const pressMentionLogoSquareSx = {
    width: PRESS_MENTION_ICON_PX,
    height: PRESS_MENTION_ICON_PX,
    borderRadius: '8px',
    overflow: 'hidden',
    bgcolor: SUMMA.bgAlt,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },
} as const;

const pillButtonAccent = {
    background: SUMMA.accent,
    color: '#fff',
    borderRadius: '100px',
    px: 2,
    py: 0.75,
    height: heroButtonMinHeight,
    minHeight: heroButtonMinHeight,
    boxSizing: 'border-box' as const,
    border: '1px solid transparent',
    fontFamily: SUMMA.fontBody,
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1,
    textTransform: 'none' as const,
    boxShadow: 'none',
    '&:hover': { background: SUMMA.accentHover, boxShadow: 'none' },
};

const SummaLandingPage: React.FC = () => {
    const openContactDialog = useOpenContactDialog();
    const quotesScrollRef = useRef<HTMLDivElement>(null);
    const quotesPausedRef = useRef(false);
    const quotesResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        analytics.track(AnalyticsEvent.VIEW_LANDING_PAGE, { brand: 'summa' });
    }, []);

    useEffect(() => {
        const el = quotesScrollRef.current;
        if (!el) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) return;

        let raf = 0;
        let last = performance.now();
        const speedPxPerSec = () => Math.max(14, el.scrollWidth / 2 / 90);

        const tick = (now: number) => {
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now;
            if (!quotesPausedRef.current) {
                el.scrollLeft += speedPxPerSec() * dt;
                const half = el.scrollWidth / 2;
                if (half > 0 && el.scrollLeft >= half) {
                    el.scrollLeft -= half;
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(raf);
            if (quotesResumeTimerRef.current) clearTimeout(quotesResumeTimerRef.current);
        };
    }, []);

    const pauseQuotesAutoScroll = (ms = 0) => {
        quotesPausedRef.current = true;
        if (quotesResumeTimerRef.current) clearTimeout(quotesResumeTimerRef.current);
        if (ms > 0) {
            quotesResumeTimerRef.current = setTimeout(() => {
                quotesPausedRef.current = false;
            }, ms);
        }
    };

    const scrollQuotesByCard = (direction: -1 | 1) => {
        const el = quotesScrollRef.current;
        if (!el) return;
        pauseQuotesAutoScroll(2500);
        const card = el.querySelector<HTMLElement>('[data-press-quote-card]');
        const amount = (card?.offsetWidth ?? 320) + PRESS_QUOTE_CAROUSEL_GAP_PX;
        const half = el.scrollWidth / 2;
        if (direction < 0 && half > 0 && el.scrollLeft < amount) {
            el.scrollLeft += half;
        }
        el.scrollBy({ left: direction * amount, behavior: 'smooth' });
    };

    const openDemo = (location: string) => {
        analytics.track(AnalyticsEvent.CLICK_REQUEST_DEMO, { location });
        openContactDialog();
    };

    const renderPressQuoteCard = (item: PressQuote, keyPrefix: string) => (
        <Box key={`${keyPrefix}-${item.id}`} data-press-quote-card="" sx={pressQuoteCardSx}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <PressThumbWithBadge
                    coverUrl={item.logo ?? PRESS_LOGOS[item.linkKey]}
                    badgeUrl={item.showInstagramBadge ? INSTAGRAM_PLATFORM_BADGE : undefined}
                    sizePx={48}
                    alt=""
                />
                <Typography
                    sx={{
                        fontFamily: SUMMA.fontBody,
                        fontWeight: 400,
                        fontSize: '0.75rem',
                        color: SUMMA.mutedSoft,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        lineHeight: 1.35,
                    }}
                >
                    {item.attribution}
                </Typography>
            </Box>
            {item.quotes.map((quote, quoteIndex) => (
                <Typography key={quoteIndex} component="blockquote" sx={pressQuoteBlockquoteSx}>
                    {quote.wrapQuotes === false ? (
                        <>&quot;{highlightSummaBrand(quote.text)}</>
                    ) : (
                        <>&quot;{highlightSummaBrand(quote.text)}&quot;</>
                    )}
                </Typography>
            ))}
            <MuiLink
                href={item.href ?? PRESS_LINKS[item.linkKey]}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={keyPrefix === 'dup' ? -1 : undefined}
                sx={{
                    fontFamily: SUMMA.fontBody,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: SUMMA.accent,
                    textDecoration: 'none',
                    borderBottom: `0.5px solid rgba(101,45,208,0.4)`,
                    alignSelf: 'flex-start',
                    mt: 'auto',
                    '&:hover': {
                        color: SUMMA.accentHover,
                        borderColor: SUMMA.accent,
                    },
                }}
            >
                {item.ctaLabel ?? 'Ver noticia'}
            </MuiLink>
        </Box>
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                bgcolor: SUMMA.bg,
            }}
        >
            <Helmet>
                <title>Summa</title>
                <meta
                    name="description"
                    content="Medimos y verificamos programas, streaming en vivo y clips para darte datos verificados sobre tu marca y tu competencia."
                />
            </Helmet>

            <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* 1. Hero */}
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: SUMMA.bg,
                        px: { xs: SUMMA.sectionPaddingX, md: 4 },
                        py: 8,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            maxWidth: 960,
                            mx: 'auto',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: SUMMA.fontHeading,
                                fontWeight: 700,
                                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                                lineHeight: 1.15,
                                letterSpacing: '-0.02em',
                                color: SUMMA.text,
                                mb: 2,
                            }}
                        >
                            Descubre lo que está pasando en medios digitales
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: SUMMA.fontBody,
                                fontWeight: 400,
                                fontSize: { xs: '1rem', md: '1.125rem' },
                                lineHeight: 1.6,
                                color: SUMMA.muted,
                                mb: 4,
                                maxWidth: 720,
                                mx: 'auto',
                            }}
                        >
                            Medimos y verificamos programas, streaming en vivo y clips en YouTube,
                            Instagram y TikTok, para darte datos verificados sobre tu marca, tu
                            competencia, tu categoría y tu propio contenido.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                            }}
                        >
                            <Button
                                component={Link}
                                to="/live"
                                startIcon={
                                    <Box
                                        sx={{
                                            width: '9px',
                                            height: '9px',
                                            borderRadius: '50%',
                                            backgroundColor: '#ef5350',
                                            flexShrink: 0,
                                            '@keyframes livePulse': {
                                                '0%': { opacity: 1, transform: 'scale(1)' },
                                                '50%': { opacity: 0.4, transform: 'scale(0.7)' },
                                                '100%': { opacity: 1, transform: 'scale(1)' },
                                            },
                                            animation: 'livePulse 1.6s ease-in-out infinite',
                                        }}
                                    />
                                }
                                sx={{
                                    borderRadius: '100px',
                                    px: 2,
                                    py: 0.75,
                                    height: heroButtonMinHeight,
                                    minHeight: heroButtonMinHeight,
                                    boxSizing: 'border-box',
                                    fontFamily: SUMMA.fontBody,
                                    fontWeight: 500,
                                    fontSize: '1rem',
                                    lineHeight: 1,
                                    textTransform: 'none',
                                    color: '#ef5350',
                                    border: '1px solid rgba(239,83,80,0.4)',
                                    background: 'rgba(239,83,80,0.08)',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: 0,
                                        marginRight: 1,
                                    },
                                    '&:hover': {
                                        background: 'rgba(239,83,80,0.15)',
                                        borderColor: '#ef5350',
                                    },
                                }}
                            >
                                Livestreams En Vivo
                            </Button>
                            <Button
                                onClick={() => openDemo('summa_landing_hero')}
                                sx={pillButtonAccent}
                            >
                                Agenda una demo
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* 2. Press strip */}
                <Box
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        overflow: 'visible',
                        background: SUMMA.bg,
                        borderTop: SUMMA.border,
                        borderBottom: SUMMA.border,
                        py: 2.5,
                        px: { xs: SUMMA.sectionPaddingX, md: 4 },
                    }}
                >
                    <Container maxWidth="lg" sx={{ overflow: 'visible' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: { xs: 1.5, sm: 2 },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: SUMMA.fontBody,
                                    fontWeight: 400,
                                    fontSize: '0.75rem',
                                    color: SUMMA.mutedSoft,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                Mencionado en
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                }}
                            >
                                {PRESS_MENTIONS.map(item => {
                                    const hasBadge = 'badgeLogo' in item && Boolean(item.badgeLogo);
                                    return (
                                        <MuiLink
                                            key={item.label}
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={item.label}
                                            sx={pressMentionPillSx}
                                        >
                                            {hasBadge ? (
                                                <PressThumbWithBadge
                                                    coverUrl={item.logo}
                                                    badgeUrl={
                                                        'badgeLogo' in item
                                                            ? item.badgeLogo
                                                            : undefined
                                                    }
                                                    sizePx={PRESS_MENTION_ICON_PX}
                                                    alt={item.label}
                                                />
                                            ) : (
                                                <Box sx={pressMentionLogoSquareSx}>
                                                    <Box
                                                        component="img"
                                                        src={item.logo}
                                                        alt={item.label}
                                                    />
                                                </Box>
                                            )}
                                        </MuiLink>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Container>
                </Box>

                {/* 3. News quotes — auto-scroll marquee */}
                <Box
                    sx={{
                        width: '100%',
                        background: SUMMA.bgAlt,
                        py: { xs: 6, md: 8 },
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        ref={quotesScrollRef}
                        onMouseEnter={() => pauseQuotesAutoScroll()}
                        onMouseLeave={() => {
                            quotesPausedRef.current = false;
                        }}
                        sx={{
                            width: '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            maskImage:
                                'linear-gradient(to right, transparent, #000 4%, #000 96%, transparent)',
                            WebkitMaskImage:
                                'linear-gradient(to right, transparent, #000 4%, #000 96%, transparent)',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                width: 'max-content',
                            }}
                        >
                            {[0, 1].map(copy => (
                                <Box
                                    key={copy}
                                    sx={{
                                        display: 'flex',
                                        gap: `${PRESS_QUOTE_CAROUSEL_GAP_PX}px`,
                                        flexShrink: 0,
                                        pr: `${PRESS_QUOTE_CAROUSEL_GAP_PX}px`,
                                    }}
                                    aria-hidden={copy === 1 || undefined}
                                >
                                    {PRESS_QUOTES.map(item =>
                                        renderPressQuoteCard(item, copy === 0 ? 'a' : 'dup')
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 0.5,
                            mt: 2.5,
                        }}
                    >
                        <IconButton
                            aria-label="Citas anteriores"
                            onClick={() => scrollQuotesByCard(-1)}
                            size="small"
                            sx={{
                                color: SUMMA.mutedSoft,
                                p: 0.5,
                                '&:hover': {
                                    color: SUMMA.text,
                                    background: 'rgba(18,17,24,0.06)',
                                },
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                        <IconButton
                            aria-label="Citas siguientes"
                            onClick={() => scrollQuotesByCard(1)}
                            size="small"
                            sx={{
                                color: SUMMA.mutedSoft,
                                p: 0.5,
                                '&:hover': {
                                    color: SUMMA.text,
                                    background: 'rgba(18,17,24,0.06)',
                                },
                            }}
                        >
                            <ChevronRightIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* 4. Products section */}
                <Box
                    id="productos"
                    sx={{
                        width: '100%',
                        background: SUMMA.bg,
                        borderTop: SUMMA.border,
                        py: { xs: 6, md: 8 },
                        px: { xs: SUMMA.sectionPaddingX, md: 4 },
                    }}
                >
                    <Container maxWidth="lg">
                        <Typography
                            component="h2"
                            sx={{
                                fontFamily: SUMMA.fontHeading,
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2.25rem' },
                                lineHeight: 1.25,
                                letterSpacing: '-0.02em',
                                color: SUMMA.text,
                                textAlign: 'center',
                                maxWidth: 720,
                                mx: 'auto',
                                mb: { xs: 4, md: 5 },
                            }}
                        >
                            Hacemos medible todo lo que se dice y aparece en medios digitales
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    md: '1fr 1fr 1fr',
                                },
                                gap: 2,
                            }}
                        >
                            {PRODUCT_LANDING_CARDS.map(
                                ({ id, title, description, Icon, pagePath }) => (
                                    <Box
                                        key={id}
                                        component={Link}
                                        to={`${pagePath}#${id}`}
                                        id={id}
                                        sx={{
                                            background: SUMMA.bgAlt,
                                            borderRadius: '12px',
                                            p: { xs: 2.5, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            textAlign: 'left',
                                            scrollMarginTop: 88,
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            transition: 'background 0.15s ease',
                                            '&:hover': {
                                                background: 'rgba(101,45,208,0.08)',
                                            },
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                color: SUMMA.accent,
                                                fontSize: 28,
                                                mb: 2,
                                            }}
                                        />
                                        <Typography
                                            component="h3"
                                            sx={{
                                                fontFamily: SUMMA.fontBody,
                                                fontWeight: 700,
                                                fontSize: '1.0625rem',
                                                lineHeight: 1.3,
                                                color: SUMMA.text,
                                                mb: 1,
                                            }}
                                        >
                                            {title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: SUMMA.fontBody,
                                                fontWeight: 400,
                                                fontSize: '0.9375rem',
                                                lineHeight: 1.55,
                                                color: SUMMA.muted,
                                            }}
                                        >
                                            {description}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    </Container>
                </Box>

                {/* 5. Footer CTA */}
                <Box
                    sx={{
                        width: '100%',
                        background: SUMMA.accent,
                        borderTop: SUMMA.border,
                        py: { xs: 8, md: 10 },
                        px: { xs: SUMMA.sectionPaddingX, md: 4 },
                        textAlign: 'center',
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                            <Typography
                                component="h2"
                                sx={{
                                    fontFamily: SUMMA.fontHeading,
                                    fontWeight: 700,
                                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                                    lineHeight: 1.25,
                                    letterSpacing: '-0.02em',
                                    color: '#fff',
                                    mb: 4,
                                }}
                            >
                                Descubre lo que puedes hacer con Summa
                            </Typography>
                            <Button
                                onClick={() => openDemo('summa_landing_footer')}
                                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                                sx={{
                                    background: '#fff',
                                    color: SUMMA.accent,
                                    borderRadius: '100px',
                                    px: 2.5,
                                    py: 1,
                                    fontFamily: SUMMA.fontBody,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        background: 'rgba(255,255,255,0.92)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                Agenda una demo
                            </Button>
                        </Box>
                    </Container>
                </Box>
            </Box>
        </Box>
    );
};

export default SummaLandingPage;
